import {ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output, ViewChild,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CcBaseComponent} from "../../../crystal-elements/cc-base.component";
import {FormFieldGroupConfig} from "../../../model/api/form-field-group-config";
import {ClearingFlexService} from "../../../services/clearing/flex/clearing-flex.service";
import {CcFormDialogComponent} from "../../../crystal-elements/form-dialog/cc-form-dialog.component";
import {Trade} from "../../../model/clearing/trade";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {AppConfigService} from "../../../services/app-config.service";
import {ClearingSelectionSummary} from "../../../model/clearing/clearing-selection-summary";
import {ClearingInfoLoadRequest} from "../../../model/clearing/clearing-info-load-request";
import {Observable, Subscription} from "rxjs";
import {ApiValidation} from "../../../model/api/api-validation";
import {ClearingTradeInfoService} from "../../../services/clearing/clearing-trade-info.service";
import {FieldsService} from "../../../services/common/fields-service";
import {ProgressTracker} from "../../../model/api/progress-tracker";
import Side = Trade.Side;

@Component({
  template: '',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ]
})
export class FlexFormDialogBaseComponent extends CcBaseComponent {

  transPartKey: string = 'clearing.flex';

  formLoading: boolean = false;

  formSubmitted: boolean = true;

  formConfig: FormFieldGroupConfig[] = [];

  dialogFormTitle: string = '';

  loadingProgressValue: number = 100;

  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<boolean>();

  @ViewChild('ccFlexAddFormDialog')
  ccFormDialog: CcFormDialogComponent;

  dataToLoadCount: number = 8;

  fieldsToUpdate: string[] = [];
  tradeIds: string[] = [];

  protected summaryBuyValue: ClearingSelectionSummary = new ClearingSelectionSummary(Side.BUY);

  protected summarySellValue: ClearingSelectionSummary = new ClearingSelectionSummary(Side.SELL);

  tracker: ProgressTracker;

  private selectionSummarySubscription?: Subscription;
  private selectionSummaryRequestVersion: number = 0;

  constructor(cdref: ChangeDetectorRef,
              protected clearingFlexService: ClearingFlexService,
              messageService: MessageService,
              protected configService: AppConfigService,
              private clearingTradeInfoService: ClearingTradeInfoService,
              protected commonFieldService: CommonFieldService,
              translateService: TranslateService,
              protected fieldsService: FieldsService) {
    super(cdref, messageService, translateService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.clearingFlexService.clearStorage();
  }

  protected initConfigs() {
    super.initConfigs();

    this.loadingProgressValue = 0;

    this.tracker = this.fieldsService.createTracker();

    this.tracker.progress.subscribe(value => {
      this.loadingProgressValue = value;
      this.refreshFieldData();
    });

  }

  public refreshFieldData(): void {
    // let tmpLoadingProgressValue = this.loadingProgressValue;
    // if (tmpLoadingProgressValue != 0)
    this.loadingProgressValue += 100 / this.dataToLoadCount;
    // else
    this.ccFormDialog.reloadFieldValueMap();
    this.markForCheck();
    // this.loadingProgressValue = tmpLoadingProgressValue;
  }


  save() {
    return;
  }

  closeDialog(): void {
    if (this.ccFormDialog)
      this.ccFormDialog.reloadFieldValueMap();
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  ngOnDestroy(): void {
    this.selectionSummarySubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  protected loadFooterInfos(itemIds: string[]) {
    this.summaryBuyValue = new ClearingSelectionSummary(Side.BUY);
    this.summarySellValue = new ClearingSelectionSummary(Side.SELL);
    this.markForCheck();
    const summaryRequest: ClearingInfoLoadRequest = new ClearingInfoLoadRequest(itemIds);
    const requestVersion = ++this.selectionSummaryRequestVersion;

    this.selectionSummarySubscription?.unsubscribe();
    this.selectionSummarySubscription = this.getSelectionSummary(summaryRequest).subscribe(apiValidation => {
      if (requestVersion !== this.selectionSummaryRequestVersion) {
        return;
      }

      if (apiValidation.success) {
        const summaryBuyValueTmp = apiValidation.item.find(value => value.side == Side.BUY);
        if (summaryBuyValueTmp)
          this.summaryBuyValue = summaryBuyValueTmp
        const summarySellValueTmp = apiValidation.item.find(value => value.side == Side.SELL);
        if (summarySellValueTmp)
          this.summarySellValue = summarySellValueTmp
        this.recalculateSummaries();
      }
      this.markForCheck();
    });
  }

  protected refreshInfosFromTrades(trades: Trade[]) {
    this.summaryBuyValue = {
      apsGrpTotalVolume: 0,
      apsGrpTotalPrice: 0,
      totalVolume: 0,
      totalPrice: 0,
      selectionCount: 0,
      side: Side.BUY
    };
    this.summarySellValue = {
      apsGrpTotalVolume: 0,
      apsGrpTotalPrice: 0,
      totalVolume: 0,
      totalPrice: 0,
      selectionCount: 0,
      side: Side.SELL
    };
    // Iterate through trades and calculate summaries
    trades.forEach(trade => {
      // Determine which summary to update based on trade side
      const summary = trade.side === Side.BUY ? this.summaryBuyValue : this.summarySellValue;

      // Update total volume and price
      if (trade.volume !== undefined && trade.price !== undefined) {
        summary.totalVolume += trade.volume;
        summary.totalPrice += trade.volume * trade.price;
        summary.selectionCount++;
      }

      // Update APS (Average Price per Share) group-related values
      if (trade.avgPriceGroupId) {
        summary.apsGrpTotalVolume += trade.volume || 0;
        summary.apsGrpTotalPrice += (trade.volume || 0) * (trade.price || 0);
      }
    });
    this.markForCheck();

    // // Optional: Calculate average prices
    // this.summaryBuyValue.apsGrpTotalPrice = this.summaryBuyValue.totalVolume > 0
    //   ? this.summaryBuyValue.totalPrice / this.summaryBuyValue.totalVolume
    //   : 0;
    //
    // this.summarySellValue.apsGrpTotalPrice = this.summarySellValue.totalVolume > 0
    //   ? this.summarySellValue.totalPrice / this.summarySellValue.totalVolume
    //   : 0;
  }

  protected getSelectionSummary(summaryRequest: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionSummary[]>> {
    return this.clearingTradeInfoService.getSelectionSummary(summaryRequest);
  }

  protected recalculateSummaries() {
  }
}
